



import { Vue, Component, Prop } from 'vue-property-decorator';
import { IField } from '@/dealroom/fields/types/field';

@Component
export default class FieldSelectBox extends Vue {
  @Prop() documentPageElementId: string;
  @Prop() active: boolean;
  @Prop() fields: IField[];

  documentPageElement: HTMLElement;
  selectBoxElement: HTMLElement;
  visible = false;
  startX = 0;
  startY = 0;
  endX = 0;
  endY = 0;
  drawingInProgress = false;
  calculationInProgress = false;

  get selectBoxId(): string {
    return `select-box-${this.documentPageElementId}`;
  }

  startSelection(event: MouseEvent): void {
    if (!this.active) {
      return;
    }
    this.visible = true;
    this.startX = this.offsetX(event.clientX);
    this.startY = this.offsetY(event.clientY);
  }

  moveSelection(event: MouseEvent): void {
    if (!this.visible || this.drawingInProgress) {
      return;
    }
    window.requestAnimationFrame(() => {
      this.endX = this.offsetX(event.clientX);
      this.endY = this.offsetY(event.clientY);
      this.drawSelection();
      this.drawingInProgress = false;
    });
    this.drawingInProgress = true;
  }

  drawSelection(): void {
    const leftX: number = Math.min(this.startX, this.endX);
    const rightX: number = Math.max(this.startX, this.endX);
    const topY: number = Math.min(this.startY, this.endY);
    const bottomY: number = Math.max(this.startY, this.endY);
    this.setStyling(leftX, topY, rightX - leftX, bottomY - topY);

    if (this.calculationInProgress) {
      return;
    }
    this.calculateFieldOverlap(leftX, topY, rightX - leftX, bottomY - topY);
    this.calculationInProgress = true;
    setTimeout(() => {
      this.calculationInProgress = false;
    }, 50);
  }

  endSelection(): void {
    if (!this.visible) {
      return;
    }
    this.setStyling(0, 0, 0, 0);
    this.visible = false;
    this.$emit('endHighlight');
  }

  offsetX(x: number): number {
    return x - this.documentPageElement.getBoundingClientRect().left;
  }

  offsetY(y: number): number {
    return y - this.documentPageElement.getBoundingClientRect().top;
  }

  setStyling(left: number, top: number, width: number, height: number): void {
    this.selectBoxElement.setAttribute(
      'style',
      `left:${left}px; top:${top}px; width:${width}px; height:${height}px;`
    );
  }

  calculateFieldOverlap(left: number, top: number, width: number, height: number): void {
    if (!this.fields || !this.fields.length) {
      return;
    }
    const highlightedFields: IField[] = [];
    this.fields.forEach((field: IField) => {
      const overlap =
        field.position.left + field.position.width >= left &&
        field.position.left <= left + width &&
        field.position.top + field.position.height >= top &&
        field.position.top <= top + height;
      if (overlap) {
        highlightedFields.push(field);
      }
    });
    const ids: string[] = highlightedFields.map((field: IField) => field.id);
    this.$emit('highlight', ids);
  }

  mounted() {
    this.selectBoxElement = this.$refs[this.selectBoxId] as HTMLElement;
    this.documentPageElement = document.getElementById(this.documentPageElementId);
    this.documentPageElement.addEventListener('mousedown', this.startSelection);
    this.documentPageElement.addEventListener('mousemove', this.moveSelection);
    document.addEventListener('mouseup', this.endSelection);
  }

  beforeDestroy() {
    this.documentPageElement.removeEventListener('mousedown', this.startSelection);
    this.documentPageElement.removeEventListener('mousemove', this.moveSelection);
    document.removeEventListener('mouseup', this.endSelection);
  }
}
